import styled from "@emotion/styled";
import { Groups } from "@mui/icons-material";
import { Box, MenuItem, Select, Stack } from "@mui/material";
import { Alert, Button, Notification } from "components/UI";
import { EthiopianDatePicker } from "components/UI/DatePicker";
import { useChurchStore } from "features/churches";
import { StatInfoCard } from "features/dashboard";
import { useDenominationStore } from "features/denominations";
import i18next from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";
import { useCentralStore } from "stores";
import { useUsers, useUsersPaginated } from "../api";
import {
  CreateRoleModal,
  InviteUserModal,
  UsersLogTable,
  UsersRoleTable,
  UsersTable,
  UsersTableHeader
} from "../components";
import { useUsersStore } from "../stores";

const Wrapper = styled.div`
  padding-top: 32px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-block: 1rem 2rem;
`;

export function Users() {
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const setHeaderTitle = useCentralStore(state => state.setHeaderTitle);
  const openUsersInviteModal = useUsersStore(
    state => state.openUsersInviteModal
  );
  const navigate = useNavigate();
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );
  const { t } = useTranslation();

  useDebounce(
    () => {
      setDebouncedSearch(searchQuery);
    },
    500,
    [searchQuery]
  );

  const usersPaginatedQuery = useUsersPaginated({
    churchId: selectedChurch?.id,
    page: 1,
    pageSize: 10,
    isDenomination: window.location.pathname.includes("denomination"),
    denominationId: selectedDenomination?.id
  });

  const allUsersQuery = useUsers({
    churchId: selectedChurch?.id,
    isDenomination: window.location.pathname.includes("denomination"),
    denominationId: selectedDenomination?.id
  });

  const usersList = allUsersQuery?.data?.result || [];

  useEffect(() => {
    setHeaderTitle(t("systemAdmins.systemAdmins.SystemAdmins"));

    return () => setHeaderTitle("");
  }, [i18next.language]);

  return (
    <Wrapper>
      <Alert message={"dashboard.common.demoMessage"} />
      <Box>
        <Notification />
        <InviteUserModal />
        <CreateRoleModal />
        <StatGrid>
          <StatInfoCard
            title={t("systemAdmins.systemAdmins.TotalUser")}
            stat={usersPaginatedQuery?.data?.result?.total || 0}
            icon={<Groups />}
            color="#82DBD8"
            background="#82DBD899"
          />
        </StatGrid>
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={2}
          >
            {tabValue === 0 && (
              <Button
                type="submit"
                variant="contained"
                onClick={openUsersInviteModal}
              >
                {t("systemAdmins.systemAdmins.InviteUser")}
              </Button>
            )}
            {tabValue === 1 && (
              <>
                <Select
                  size="small"
                  value={selectedUser}
                  onChange={e => setSelectedUser(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">{t("systemAdmins.logs.All")}</MenuItem>
                  {usersList.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </MenuItem>
                  ))}
                </Select>
                <EthiopianDatePicker
                  label={t("systemAdmins.logs.StartDate")}
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  width="auto"
                />
                <EthiopianDatePicker
                  label={t("systemAdmins.logs.EndDate")}
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  width="auto"
                />
              </>
            )}
            {tabValue === 2 && (
              <Button
                type="submit"
                variant="contained"
                onClick={() => {
                  if (window.location.pathname.includes("denomination")) {
                    navigate(
                      `/denomination-dashboard/${selectedDenomination?.id}/roles/create`
                    );
                  } else {
                    navigate("/roles/create");
                  }
                }}
              >
                {t("systemAdmins.systemAdmins.CreateNewRole")}
              </Button>
            )}
          </Stack>
          <Box>
            <UsersTableHeader
              tabValue={tabValue}
              setTabValue={setTabValue}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
            {tabValue === 0 && <UsersTable />}
            {tabValue === 1 && (
              <UsersLogTable
                searchQuery={debouncedSearch}
                selectedUser={selectedUser}
                startDate={startDate}
                endDate={endDate}
              />
            )}
            {tabValue === 2 && <UsersRoleTable />}
          </Box>
        </Stack>
      </Box>
    </Wrapper>
  );
}
